import { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatCoins } from "../lib/format";
import { playSpin, playWin, playLose } from "../lib/sound";
import Confetti from "./Confetti";

const COLORS = ["#FF8A1F", "#13205a", "#FFC21A", "#0b1640", "#E0611A", "#1c2c78"];
const R = 140;

const polar = (deg, r = R) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [R + r * Math.cos(a), R + r * Math.sin(a)];
};

const Trishul = () => (
  <svg width="38" height="56" viewBox="0 0 38 56" className="drop-shadow-[0_0_10px_rgba(255,170,40,0.9)]">
    <path d="M19 56 L19 18 M6 4 Q4 18 19 22 Q34 18 32 4 M19 0 L19 22" stroke="#FFD54A" strokeWidth="4" fill="none" strokeLinecap="round" />
    <path d="M13 38 h12 l-2 4 h-8 z M13 46 h12 l-2 -4" fill="#FF8A1F" />
  </svg>
);

const fmt = (s) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((n) => String(n).padStart(2, "0")).join(":");

export default function BholeSpin() {
  const { setCoins } = useAuth();
  const { lang } = useLang();
  const L = (hi, en) => (lang === "hi" ? hi : en);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fx, setFx] = useState(0);
  const [last, setLast] = useState(null);
  const controls = useAnimation();
  const rot = useRef(0);

  useEffect(() => { api.get("/bhole-spin/status").then(({ data }) => setSt(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!st || st.can_claim) return;
    const id = setInterval(() => setSt((s) => (s.seconds_left > 1 ? { ...s, seconds_left: s.seconds_left - 1 } : { ...s, can_claim: true, seconds_left: 0 })), 1000);
    return () => clearInterval(id);
  }, [st?.can_claim]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!st?.enabled || !st.prizes?.length) return null;
  const slice = 360 / st.prizes.length;

  const spin = async () => {
    setBusy(true); setLast(null); playSpin();
    try {
      const { data } = await api.post("/bhole-spin");
      const center = data.index * slice + slice / 2;
      const final = rot.current - (rot.current % 360) + 360 * 6 + (360 - center);
      rot.current = final;
      await controls.start({ rotate: final, transition: { duration: 4.5, ease: [0.17, 0.67, 0.12, 0.99] } });
      setCoins(data.coins); setLast(data);
      setSt((s) => ({ ...s, can_claim: false, seconds_left: data.seconds_left }));
      if (data.win > 0) { setFx(Date.now()); playWin(data.win); toast.success(`${L("भोले का आशीर्वाद", "Bhole's blessing")} +${formatCoins(data.win)}!`); }
      else { playLose(); toast(L("आज किस्मत ने साथ नहीं दिया, कल फिर आएं!", "No luck today, come back tomorrow!")); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <section className="mw-card p-6 relative overflow-hidden" data-testid="bhole-spin-card">
      <Confetti fire={fx} />
      <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
        <div className="relative mx-auto" style={{ width: R * 2, height: R * 2 + 20 }}>
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10 rotate-180"><Trishul /></div>
          <motion.svg animate={controls} width={R * 2} height={R * 2} viewBox={`0 0 ${R * 2} ${R * 2}`} className="mt-5 rounded-full ring-4 ring-amber-400/60 shadow-[0_0_40px_rgba(255,138,31,0.35)]" data-testid="bhole-spin-wheel">
            {st.prizes.map((p, i) => {
              const [x1, y1] = polar(i * slice); const [x2, y2] = polar((i + 1) * slice);
              const [tx, ty] = polar(i * slice + slice / 2, R * 0.66);
              const big = slice > 180 ? 1 : 0;
              return (
                <g key={i}>
                  <path d={`M${R},${R} L${x1},${y1} A${R},${R} 0 ${big} 1 ${x2},${y2} Z`} fill={COLORS[i % COLORS.length]} stroke="#FFD54A" strokeWidth="1.5" />
                  <text x={tx} y={ty} fill={i % 2 ? "#FFE08A" : "#0b1640"} fontSize="13" fontWeight="800" textAnchor="middle" dominantBaseline="middle"
                    transform={`rotate(${i * slice + slice / 2} ${tx} ${ty})`}>{p.label}</text>
                </g>
              );
            })}
            <circle cx={R} cy={R} r="24" fill="#070B1F" stroke="#FFD54A" strokeWidth="3" />
            <text x={R} y={R} fill="#FFB566" fontSize="11" fontWeight="900" textAnchor="middle" dominantBaseline="middle">ॐ</text>
          </motion.svg>
        </div>
        <div className="space-y-4 text-center md:text-left">
          <p className="om-chant font-display text-xs font-bold">॥ हर हर महादेव ॥</p>
          <h2 className="font-display text-3xl font-extrabold gold-text">Bhole Daily Spin</h2>
          <p className="text-sm text-slate-300">{L("हर दिन एक मुफ़्त स्पिन। भोले की कृपा से जीतें असली ₹ इनाम!", "One free spin every day. Win real ₹ prizes with Bhole's blessing!")}</p>
          {last && <p className="font-display text-xl font-bold" data-testid="bhole-spin-result" style={{ color: last.win > 0 ? "#00E676" : "#FF6B8A" }}>{last.win > 0 ? `+${formatCoins(last.win)}` : last.label}</p>}
          {st.can_claim ? (
            <button onClick={spin} disabled={busy} data-testid="bhole-spin-btn" className="btn-gold rounded-full px-8 py-3 text-lg inline-flex items-center gap-2 animate-pulse-glow">
              {busy ? <Loader2 size={18} className="animate-spin" /> : null} {L("मुफ़्त स्पिन करें", "Spin for free")}
            </button>
          ) : (
            <p className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-black/30 px-5 py-2.5 text-sm text-amber-200" data-testid="bhole-spin-countdown">
              <Clock size={15} /> {L("अगला स्पिन", "Next spin in")} <b className="font-mono-num">{fmt(st.seconds_left)}</b>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
